package org.maplibre.compose.gljs

import web.gl.WebGL2RenderingContext

/** The worker built with the embedded engine, shared by every map in this module. */
internal val DefaultWorkerUrl: String by lazy { getDefaultWorkerUrl() }

/**
 * A same-origin worker URL for [workerUrl]. A cross-origin URL becomes a blob that `import`s the
 * absolute form of that URL, which is what MapLibre GL JS 6 does for a CDN worker. Same-origin URLs
 * are returned as they are.
 *
 * MapLibre wraps worker URLs with `new URL(url, import.meta.url)`. Webpack rewrites that into a
 * module lookup, which fails for an `https` URL with "Cannot find module". This path avoids
 * `import.meta.url` so custom cross-origin workers work when the library is bundled.
 */
internal fun sameOriginWorkerUrl(workerUrl: String): String =
  js(
      """
      (function() {
        var loc = globalThis.location;
        if (!loc) return workerUrl;
        var resolvedWorkerUrl;
        try {
          resolvedWorkerUrl = new URL(workerUrl, loc.href);
          if (resolvedWorkerUrl.origin === loc.origin) return workerUrl;
        } catch (e) {
          return workerUrl;
        }
        return URL.createObjectURL(
          new Blob(
            ["import " + JSON.stringify(resolvedWorkerUrl.href)],
            {type: "text/javascript"}
          )
        );
      })()
      """,
    )
    .unsafeCast<String>()

/**
 * Adapts MapLibre GL JS internals for rendering into Compose. Compatibility checks detect changes
 * to the internals used here.
 */
internal object GlJsRuntime {

  private var workerUrlConfigured = false

  /** Points MapLibre GL JS 6 at [workerUrl]. The first call wins; later calls are ignored. */
  fun pointAtWorker(workerUrl: String) {
    if (workerUrlConfigured) return
    setWorkerUrl(sameOriginWorkerUrl(workerUrl))
    workerUrlConfigured = true
  }

  /** Runs [build] with every WebGL context request on the page answered by [gl]. */
  fun <T> lendingContext(gl: WebGL2RenderingContext, build: () -> T): T {
    val prototype = js("HTMLCanvasElement").prototype
    val original = prototype.getContext
    var lent = 0
    prototype.getContext = { type: String, options: dynamic ->
      // `this` is the canvas being asked; skiko's own canvas must keep answering for itself.
      val receiver = js("this")
      if (receiver !== gl.canvas && (type == "webgl2" || type == "webgl")) {
        lent++
        gl
      } else {
        original.call(receiver, type, options)
      }
    }
    try {
      return build()
    } finally {
      prototype.getContext = original
      check(lent == 1) {
        "MapLibre asked for $lent WebGL contexts while its map was being created, not one. It no " +
          "longer takes its context from its canvas the way this platform assumes."
      }
    }
  }

  /**
   * Runs [draw] while [gl] reports [width] and [height] as its drawing buffer size.
   *
   * MapLibre normally renders into its context's canvas, so some fullscreen passes read the canvas
   * drawing buffer instead of the current framebuffer. The Compose compositor redirects that
   * framebuffer to a map-sized texture inside a larger shared canvas.
   */
  fun <T> withDrawingBufferSize(
    gl: WebGL2RenderingContext,
    width: Int,
    height: Int,
    draw: () -> T,
  ): T {
    val target = gl.asDynamic()
    val objects = js("Object")
    val previousWidth = objects.getOwnPropertyDescriptor(target, "drawingBufferWidth")
    val previousHeight = objects.getOwnPropertyDescriptor(target, "drawingBufferHeight")
    val widthDescriptor = js("({configurable: true})")
    val heightDescriptor = js("({configurable: true})")
    widthDescriptor.value = width
    heightDescriptor.value = height
    try {
      objects.defineProperty(target, "drawingBufferWidth", widthDescriptor)
      objects.defineProperty(target, "drawingBufferHeight", heightDescriptor)
      return draw()
    } finally {
      restoreOwnProperty(objects, target, "drawingBufferWidth", previousWidth)
      restoreOwnProperty(objects, target, "drawingBufferHeight", previousHeight)
    }
  }

  private fun restoreOwnProperty(objects: dynamic, target: dynamic, name: String, value: dynamic) {
    if (value == null || value == undefined) {
      js("Reflect").deleteProperty(target, name)
    } else {
      objects.defineProperty(target, name, value)
    }
  }

  /**
   * `bindFramebuffer.set(null)` is the one place in MapLibre's renderer that means "the screen".
   * [target] is read per call, because a resize replaces the framebuffer while the map renders.
   */
  fun redirectDefaultFramebuffer(context: Context, target: () -> Any?) {
    val binding = context.asDynamic().bindFramebuffer
    check(jsTypeOf(binding.set) == "function") {
      "MapLibre's Context.bindFramebuffer no longer has a set method to redirect"
    }
    binding.set = { requested: dynamic ->
      val self = js("this")
      val next = if (requested == null || requested == undefined) target() else requested
      if (next !== self.current || self.dirty == true) {
        self.gl.bindFramebuffer(self.gl.FRAMEBUFFER, next)
        self.current = next
        self.dirty = false
      }
    }
  }

  /** `Map.remove` ends by losing its context, which is fatal here: the context is Compose's. */
  fun removingWithoutLosingContext(context: WebGL2RenderingContext, remove: () -> Unit) {
    val gl = context.asDynamic()
    val original = gl.getExtension
    gl.getExtension = { name: String ->
      if (name == "WEBGL_lose_context") null else original.call(gl, name)
    }
    try {
      remove()
    } finally {
      gl.getExtension = original
    }
  }

  /** `triggerRepaint` is MapLibre's only caller of `browser.frame`. */
  fun interceptRepaintRequests(map: MaplibreMap, onRequest: () -> Unit) {
    val dynamicMap = map.asDynamic()
    check(jsTypeOf(dynamicMap.triggerRepaint) == "function") {
      "MapLibre's Map no longer has a triggerRepaint method to intercept"
    }
    dynamicMap.triggerRepaint = { onRequest() }
  }
}
